import { useEffect, useState } from "react";
import { CloudDownload, CloudOff, Check, RefreshCw, WifiOff, Plane, MapPin, Phone, FileText, BookOpen } from "lucide-react";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/* Offline Travel Mode — one tap pre-downloads EVERYTHING this trip could ever
   need during a flight or no-wifi country stop: itinerary, day plan, documents,
   attachments, reminders, flight details, hotel info, destination/city/country
   guides, airport reference pages, airline contacts, embassy/emergency numbers,
   visa summary, loyalty programs. Every GET flows through the service worker,
   which keeps a network-first copy for offline replay. */

const SEG_FIELDS = ["carrier", "iata", "flight", "number", "from", "to", "from_place", "to_place"];
function extractCodes(trip) {
  const codes = new Set();
  const names = new Set();
  for (const s of trip.segments || []) {
    for (const k of SEG_FIELDS) {
      const v = s[k];
      if (!v) continue;
      const str = String(v).trim();
      if (/^[A-Z]{3}$/.test(str.toUpperCase())) codes.add(str.toUpperCase());
      else if (/^[A-Z]{2}\d/.test(str.toUpperCase())) names.add(str.slice(0, 2).toUpperCase());
      else names.add(str);
    }
  }
  for (const stop of trip.stops || []) names.add(stop);
  return { codes: [...codes], names: [...names] };
}

export default function OfflineTripKit({ trip }) {
  const [state, setState] = useState(() => {
    try { return JSON.parse(localStorage.getItem(`tp_offline_kit_${trip.id}`) || "null"); } catch { return null; }
  });
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [online, setOnline] = useState(navigator.onLine);

  useEffect(() => {
    const up = () => setOnline(true), down = () => setOnline(false);
    window.addEventListener("online", up); window.addEventListener("offline", down);
    return () => { window.removeEventListener("online", up); window.removeEventListener("offline", down); };
  }, []);

  const save = async () => {
    if (!navigator.onLine) { toast.error("You're offline — connect to sync this trip first"); return; }
    setBusy(true); setProgress(0);
    const token = localStorage.getItem("token") || "";
    const headers = { Authorization: `Bearer ${token}` };
    const { codes, names } = extractCodes(trip);

    const base = [
      // Core trip data
      `${BACKEND}/api/trips/${trip.id}`,
      `${BACKEND}/api/trips/${trip.id}/documents`,
      `${BACKEND}/api/trips/${trip.id}/overview`,
      `${BACKEND}/api/trips/${trip.id}/readiness`,
      `${BACKEND}/api/trips/${trip.id}/layovers`,
      `${BACKEND}/api/trips/${trip.id}/emergency`,
      `${BACKEND}/api/trips/${trip.id}/offline-pack`,
      `${BACKEND}/api/remind-me`,
      `${BACKEND}/api/trips`,
      // Reference data traveller needs mid-flight
      `${BACKEND}/api/loyalty/programs`,
    ];
    // Airport pages for every IATA code in the itinerary
    const airportUrls = codes.map((c) => `${BACKEND}/api/airports/${c}`);
    const loungeUrls = codes.map((c) => `${BACKEND}/api/airport-lounges/${c}`);
    // Country pages for every stop (uppercase + title case to cover both route shapes)
    const countryUrls = (trip.stops || []).flatMap((s) => [
      `${BACKEND}/api/countries/${encodeURIComponent(s)}`,
      `${BACKEND}/api/destinations/${encodeURIComponent(s.toLowerCase())}`,
      `${BACKEND}/api/city/${encodeURIComponent(s.toLowerCase())}`,
    ]);
    // Attachment image blobs
    const attachUrls = (trip.attachments || []).map((a) =>
      `${BACKEND}/api/trips/attachments/${a.id}/image?auth=${token}`);

    const urls = [...base, ...airportUrls, ...loungeUrls, ...countryUrls, ...attachUrls];
    let ok = 0;
    for (let i = 0; i < urls.length; i += 6) {
      const batch = urls.slice(i, i + 6);
      await Promise.all(batch.map((u) =>
        fetch(u, { headers, credentials: "include" })
          .then((r) => { if (r.ok) ok += 1; })
          .catch(() => {})
      ));
      setProgress(Math.round(((i + batch.length) / urls.length) * 100));
    }
    const next = {
      saved_at: new Date().toISOString(), cached: ok, total: urls.length,
      files: (trip.attachments || []).length, codes: codes.length, countries: (trip.stops || []).length,
    };
    try { localStorage.setItem(`tp_offline_kit_${trip.id}`, JSON.stringify(next)); } catch { /* storage full */ }
    // Register in the global index so /offline can list all cached trips
    try {
      const idx = JSON.parse(localStorage.getItem("tp_offline_trips") || "{}");
      idx[trip.id] = { title: trip.title, stops: trip.stops || [], start_date: trip.start_date, saved_at: next.saved_at };
      localStorage.setItem("tp_offline_trips", JSON.stringify(idx));
    } catch { /* ignore */ }
    setState(next);
    setBusy(false); setProgress(0);
    toast.success(`${ok} items cached — flight details, docs, airports, destinations & contacts open with no internet`);
  };

  if (!("serviceWorker" in navigator)) return null;

  return (
    <div className="mb-8 border-2 border-sky-300 bg-sky-50/60 rounded-2xl p-4 sm:p-5" data-testid="offline-trip-kit">
      <div className="flex flex-wrap items-start gap-3">
        {state ? <Check className="w-5 h-5 text-emerald-600 shrink-0 mt-1" /> : <CloudOff className="w-5 h-5 text-sky-600 shrink-0 mt-1" />}
        <div className="flex-1 min-w-0">
          <div className="font-700 text-sm text-sky-900 flex items-center gap-2 flex-wrap">
            {state ? "Available offline — everything you need in the air" : "Offline Travel Mode"}
            {!online && <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase bg-amber-100 border border-amber-300 text-amber-800 px-1.5 py-0.5 rounded" data-testid="offline-kit-offline-badge"><WifiOff className="w-3 h-3" /> offline now</span>}
          </div>
          <div className="text-xs text-muted-foreground mt-0.5">
            {state
              ? `Synced ${new Date(state.saved_at).toLocaleString()} — ${state.cached} items including flight details, documents, airport pages & destination guides.`
              : "One tap pre-downloads flights, documents, airport pages, destination guides, embassy & airline contacts — so you can open everything mid-flight or without wifi."}
          </div>
        </div>
        <button onClick={save} disabled={busy} data-testid="offline-kit-save"
          className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-2 text-sm font-700 rounded-full transition-colors disabled:opacity-60 ${state ? "border border-sky-400 text-sky-700 hover:bg-sky-100" : "bg-sky-600 text-white hover:bg-sky-700"}`}>
          {busy ? <RefreshCw className="w-4 h-4 animate-spin" /> : (state ? <RefreshCw className="w-4 h-4" /> : <CloudDownload className="w-4 h-4" />)}
          {busy ? (progress ? `Syncing… ${progress}%` : "Syncing…") : (state ? "Re-sync" : "Save for offline")}
        </button>
      </div>
      {state && (
        <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px] text-sky-900">
          <Chip icon={Plane} text={`${state.codes || 0} airports`} />
          <Chip icon={MapPin} text={`${state.countries || 0} destinations`} />
          <Chip icon={FileText} text={`${state.files || 0} documents`} />
          <Chip icon={Phone} text="Emergency contacts" />
        </div>
      )}
      {!state && (
        <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-sky-800">
          <span className="inline-flex items-center gap-1"><Plane className="w-3 h-3" /> Flight details</span>
          <span className="inline-flex items-center gap-1"><FileText className="w-3 h-3" /> Documents & tickets</span>
          <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> Destination guides</span>
          <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" /> Airline & embassy contacts</span>
          <span className="inline-flex items-center gap-1"><BookOpen className="w-3 h-3" /> Visa & lounge info</span>
        </div>
      )}
    </div>
  );
}

function Chip({ icon: Icon, text }) {
  return (
    <span className="inline-flex items-center gap-1.5 bg-white/70 border border-sky-200 rounded-full px-2 py-1">
      <Icon className="w-3 h-3 text-sky-700" /> {text}
    </span>
  );
}
